import axios from "axios";
import AuthService from "./AuthService";
const apiClient = axios.create({
  baseURL: "/api",
  withCredentials: false,
});

export default {
  async getTokenHeader() {
    return {
      headers: AuthService.getAuthHeader(),
    };
  },

  getCurrentTenantId() {
    try {
      const stored = sessionStorage.getItem("current_tenant");
      return stored ? JSON.parse(stored).tenant_id : null;
    } catch {
      return null;
    }
  },

  // ============================================
  // OAUTH / AUTH
  // ============================================

  async getCurrentUser() {
    const header = await this.getTokenHeader();
    return apiClient.get("/oauth/me", header);
  },

  // ============================================
  // CHAT / ASSISTANT
  // ============================================

  async startChatSession(assistantId) {
    const header = await this.getTokenHeader();
    return apiClient.get("/chat/start-session", {
      ...header,
      params: { assistant_id: assistantId, tenant_id: this.getCurrentTenantId() },
    });
  },

  async sendChatPrompt(sessionId, message) {
    const header = await this.getTokenHeader();
    return apiClient.post(
      "/chat/prompt",
      { prompt: message },
      { ...header, params: { session_id: sessionId, tenant_id: this.getCurrentTenantId() } }
    );
  },

  async getChatMessages(sessionId) {
    const header = await this.getTokenHeader();
    return apiClient.get("/chat/messages", {
      ...header,
      params: { session_id: sessionId, tenant_id: this.getCurrentTenantId() },
    });
  },

  async createAssistant(assistantData) {
    const header = await this.getTokenHeader();
    return apiClient.post("/chat/create-assistant", assistantData, header);
  },

  async getAssistant(assistantId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/chat/get-assistant/${assistantId}`, header);
  },

  async synthesizeSpeech(text, voiceId = null) {
    const header = await this.getTokenHeader();
    return apiClient.post("/chat/synth", { text, voice_id: voiceId }, header);
  },

  async recognizeSpeech(audioFile) {
    const header = await this.getTokenHeader();
    const formData = new FormData();
    formData.append("file", audioFile);
    return apiClient.post("/chat/recognize", formData, {
      ...header,
      headers: {
        ...header.headers,
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // ============================================
  // PROJECTS
  // ============================================

  async createProject(projectData) {
    const header = await this.getTokenHeader();
    return apiClient.post("/projects/", projectData, header);
  },

  async updateProject(projectId, projectData) {
    const header = await this.getTokenHeader();
    return apiClient.put(`/projects/${projectId}`, projectData, header);
  },

  async activateProject(projectId) {
    const header = await this.getTokenHeader();
    return apiClient.put(`/projects/${projectId}/activate`, null, header);
  },

  async getPublicProject(projectId) {
    return apiClient.get(`/projects/${projectId}/public`);
  },

  async getProject(projectId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/projects/${projectId}`, header);
  },

  async getProjectInterviews(projectId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/projects/${projectId}/interviews`, header);
  },

  async getAllProjects() {
    const header = await this.getTokenHeader();
    return apiClient.get("/projects/", header);
  },

  // ============================================
  // INTERVIEWS
  // ============================================

  async createInterview(projectId) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/interviews/new/${projectId}`, {}, header);
  },

  async getInterview(sessionId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/interviews/${sessionId}`, header);
  },

  async getInterviewState(sessionId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/interviews/${sessionId}/state`, header);
  },

  async submitAnswer(sessionId, answer) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/interviews/${sessionId}/answer`, { answer }, header);
  },

  async completeInterview(sessionId) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/interviews/${sessionId}/complete`, {}, header);
  },

  // ============================================
  // ANALYTICS
  // ============================================

  async recordAnalyticsEvent(eventData) {
    const header = await this.getTokenHeader();
    return apiClient.post("/analytics/events/new", eventData, header);
  },

  async getDailyEventAggregations(filters) {
    const header = await this.getTokenHeader();
    return apiClient.post("/analytics/events/daily", filters, header);
  },

  async getLiveProjects() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/projects/live", header);
  },

  async getLiveAssistants() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/projects/live/assistants", header);
  },

  async getAllProjectSessionCounts() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/projects/live/session/counts", header);
  },

  async getAllProjectInterviewCounts() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/projects/live/interviews/counts", header);
  },

  async getSessionsSummary() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/sessions/summary", header);
  },

  async getUserActivity() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/users/activity", header);
  },

  async getAssistantPerformance() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/assistants/performance", header);
  },

  async getHourlyActivity() {
    const header = await this.getTokenHeader();
    return apiClient.get("/analytics/hourly-activity", header);
  },

  // ============================================
  // BILLING / PAYMENTS
  // ============================================

  async getProducts(appCode) {
    return apiClient.get(`/payments/products?app=${appCode}`);
  },

  async getTenantSubscription() {
    const header = await this.getTokenHeader();
    return apiClient.get("/payments/tenant/subscription", header);
  },

  async createTenantCheckoutSession(priceIds) {
    const header = await this.getTokenHeader();
    return apiClient.post(
      "/payments/tenant/create-checkout-session",
      { price_ids: priceIds },
      header
    );
  },

  async verifyTenantSession(sessionId) {
    const header = await this.getTokenHeader();
    return apiClient.post("/payments/tenant/verify-session", { session_id: sessionId }, header);
  },

  async cancelTenantSubscription(subscriptionId) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/payments/tenant/subscriptions/${subscriptionId}/cancel`, {}, header);
  },

  async reactivateTenantSubscription(subscriptionId) {
    const header = await this.getTokenHeader();
    return apiClient.post(
      `/payments/tenant/subscriptions/${subscriptionId}/reactivate`,
      {},
      header
    );
  },

  async getTenantInvoices() {
    const header = await this.getTokenHeader();
    return apiClient.get("/payments/invoices", header);
  },

  // ============================================
  // MESSAGING
  // ============================================

  async sendEmail(subject, body) {
    const header = await this.getTokenHeader();
    return apiClient.post("/message/email", { subject, body }, header);
  },

  // ============================================
  // TENANT APPS
  // ============================================

  async getTenant(tenantId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/tenant/${tenantId}`, header);
  },

  async installApp(appName) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/tenant/apps/install/${appName}`, null, header);
  },

  // ============================================
  // TABLE OPERATIONS (Boards)
  // ============================================

  async createTable(tableData) {
    const header = await this.getTokenHeader();
    return apiClient.post("/table/", tableData, header);
  },

  async getTables() {
    const header = await this.getTokenHeader();
    return apiClient.get("/table/", header);
  },

  async getTable(tableName) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/table/${tableName}`, header);
  },

  async updateTable(tableName, tableData) {
    const header = await this.getTokenHeader();
    return apiClient.put(`/table/${tableName}`, tableData, header);
  },

  async deleteTable(tableName) {
    const header = await this.getTokenHeader();
    return apiClient.delete(`/table/${tableName}`, header);
  },

  // Creates a new table definition AND bulk-loads its data from a file in one
  // call. The backend infers field names/types from the file. Returns
  // { table: TableDefinition, rows_imported }. 400 if the table name exists.
  async importTable(file, { tableName, tableLabel, tableType } = {}) {
    const header = await this.getTokenHeader();
    const formData = new FormData();
    formData.append("file", file);
    if (tableName) formData.append("table_name", tableName);
    if (tableLabel) formData.append("table_label", tableLabel);
    if (tableType) formData.append("table_type", tableType);
    return apiClient.post("/import/table", formData, {
      ...header,
      headers: { ...header.headers, "Content-Type": "multipart/form-data" },
    });
  },

  // Bulk-loads records from a file into an EXISTING table. The schema is not
  // modified — file columns are matched to existing field names (unmatched
  // columns are skipped). Returns { table, rows_imported }.
  async importRecords(tableName, file) {
    const header = await this.getTokenHeader();
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/data/table/${tableName}/import`, formData, {
      ...header,
      headers: { ...header.headers, "Content-Type": "multipart/form-data" },
    });
  },

  // ============================================
  // VIEW OPERATIONS
  // ============================================

  async createView(viewData) {
    const header = await this.getTokenHeader();
    return apiClient.post("/view/", viewData, header);
  },

  async getViews() {
    const header = await this.getTokenHeader();
    return apiClient.get("/view/", header);
  },

  async getView(viewName) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/view/${viewName}`, header);
  },

  async updateView(viewName, viewData) {
    const header = await this.getTokenHeader();
    return apiClient.put(`/view/${viewName}`, viewData, header);
  },

  async deleteView(viewName) {
    const header = await this.getTokenHeader();
    return apiClient.delete(`/view/${viewName}`, header);
  },

  // ============================================
  // DATA OPERATIONS (Cards/Records)
  // ============================================

  // Reference fields hold raw ids. List reads pass resolve=all so the API
  // attaches each referenced document under the record's _resolved key; the
  // client-side lookup (see LookupCache) remains as the editing/fallback path.
  async createRecord(tableName, recordData) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/table/${tableName}`, recordData, header);
  },

  async getRecords(tableName, skip = 0, limit = 100, { allRows = false } = {}) {
    const header = await this.getTokenHeader();
    const params = { skip, limit, resolve: "all" };
    if (allRows) params.rows = "all";
    return apiClient.get(`/data/table/${tableName}`, {
      ...header,
      params,
    });
  },

  async queryRecords(tableName, filters, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/table/${tableName}/query`, { filters, skip, limit }, header);
  },

  async getRecord(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/data/table/${tableName}/${recordId}`, header);
  },

  async updateRecord(tableName, recordId, recordData) {
    const header = await this.getTokenHeader();
    return apiClient.put(`/data/table/${tableName}/${recordId}`, recordData, header);
  },

  async deleteRecord(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.delete(`/data/table/${tableName}/${recordId}`, header);
  },

  async retryRecord(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/table/${tableName}/${recordId}/retry`, {}, header);
  },

  async revertRule(tableName, recordId, ruleKey) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/table/${tableName}/${recordId}/revert/${ruleKey}`, {}, header);
  },

  async listAttachments(tableName, recordId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/data/table/${tableName}/${recordId}/attachments`, header);
  },

  async uploadAttachment(tableName, recordId, file) {
    const header = await this.getTokenHeader();
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/data/table/${tableName}/${recordId}/attachments`, formData, {
      ...header,
      headers: { ...header.headers, "Content-Type": "multipart/form-data" },
    });
  },

  async downloadAttachment(tableName, recordId, filename) {
    const header = await this.getTokenHeader();
    return apiClient.get(
      `/data/table/${tableName}/${recordId}/attachments/${encodeURIComponent(filename)}`,
      { ...header, responseType: "blob" }
    );
  },

  async deleteAttachment(tableName, recordId, filename) {
    const header = await this.getTokenHeader();
    return apiClient.delete(
      `/data/table/${tableName}/${recordId}/attachments/${encodeURIComponent(filename)}`,
      header
    );
  },

  async getViewData(viewName, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/data/view/${viewName}`, {
      ...header,
      params: { skip, limit },
    });
  },

  async queryViewData(viewName, filters, skip = 0, limit = 100) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/view/${viewName}/query`, { filters, skip, limit }, header);
  },

  // ============================================
  // HUMAN TASKS
  // ============================================

  async getMyTasks(status = "open") {
    const header = await this.getTokenHeader();
    return apiClient.get("/data/tasks", { ...header, params: { status } });
  },

  // Open tasks for a whole board (record_id omitted) or a single record.
  async getBoardTasks(tableName, recordId = null, status = "open") {
    const header = await this.getTokenHeader();
    const params = { status, table: tableName };
    if (recordId) params.record_id = recordId;
    return apiClient.get("/data/tasks", { ...header, params });
  },

  async completeTask(taskId) {
    const header = await this.getTokenHeader();
    return apiClient.post(`/data/task/${taskId}/complete`, {}, header);
  },

  // ============================================
  // SECRETS
  // ============================================

  async getSecrets() {
    const header = await this.getTokenHeader();
    return apiClient.get("/secrets", header);
  },

  async createSecret(data) {
    const header = await this.getTokenHeader();
    return apiClient.post("/secrets", data, header);
  },

  async deleteSecret(name) {
    const header = await this.getTokenHeader();
    return apiClient.delete(`/secrets/${name}`, header);
  },

  // ============================================
  // AUTOMATION RULES
  // ============================================

  async getAutomationRules() {
    const header = await this.getTokenHeader();
    return apiClient.get("/automation/rule", header);
  },

  async getAutomationRule(ruleId) {
    const header = await this.getTokenHeader();
    return apiClient.get(`/automation/rule/${ruleId}`, header);
  },

  async createAutomationRule(data) {
    const header = await this.getTokenHeader();
    return apiClient.post("/automation/rule", data, header);
  },

  async updateAutomationRule(ruleId, data) {
    const header = await this.getTokenHeader();
    return apiClient.put(`/automation/rule/${ruleId}`, data, header);
  },

  async deleteAutomationRule(ruleId) {
    const header = await this.getTokenHeader();
    return apiClient.delete(`/automation/rule/${ruleId}`, header);
  },
};
